import {
  types,
  getParent,
  getParentOfType,
  clone,
  type IAnyModelType,
} from 'mobx-state-tree';
import EventType from '../metamodel/event_type';
import { orderBy } from 'lodash';
import { modelReference } from '../refHelper';
import OET from './oet';
import type { IEventType } from '../metamodel/event_type';
import type { IObjectType } from '../metamodel/object_type';
import type { IOET } from './oet';

/**
 * The OETRow instance interface.
 *
 * HAND-DECLARED because OETRow self-references three ways (the §3 condition): it
 * embeds `subRows: OETRow[]`, the exported `compareSiblingRows` helper takes two
 * `IOETRow`s, and it reaches the `IOET` container through `getParentOfType`. The
 * interface lets every cross-reference resolve lazily without re-inferring
 * `typeof OETRow`.
 */
export interface IOETRow {
  readonly event: IEventType;
  readonly subRows: IOETRow[];
  readonly lowerCaseEventName: string;
  readonly owningObjectsWithColumnIndex: { object: IObjectType; columnIndex: number }[];
  readonly firstOwningObject: IObjectType | null;
  readonly owningObjectColumnIndex: number;
  readonly owningMethodsPriority: number;
  readonly index: number;
  readonly flattenedRows: IOETRow[];
  readonly dndGroup: string;
  addSubRow(event: string): void;
  removeSubRow(event: string): IOETRow;
  moveSubRow(from: number, to: number): void;
  sortSubRows(): void;
}

export function compareSiblingRows(first: IOETRow, second: IOETRow) {
  if (first.owningObjectColumnIndex === second.owningObjectColumnIndex) {
    if (first.owningMethodsPriority === second.owningMethodsPriority) {
      return first.lowerCaseEventName >= second.lowerCaseEventName ? 1 : -1;
    } else {
      return first.owningMethodsPriority - second.owningMethodsPriority;
    }
  } else {
    return first.owningObjectColumnIndex - second.owningObjectColumnIndex;
  }
}

/**
 * A row in the OET.
 *
 * For the reference to the event type, we don't use types.reference directly,
 * but our custom wrapper. See '../refHelper.js' for the reason why.
 */
const OETRow = types
  .model('OETRow', {
    event: modelReference<typeof EventType>(EventType),
    subRows: types.array(types.late((): IAnyModelType => OETRow)),
  })
  .views((self0) => {
    const self = self0 as unknown as IOETRow;
    return {
      // get the lowercased name of the referenced event type
      get lowerCaseEventName() {
        return self.event.name.toLowerCase();
      },
      // get the owning object types for the referenced event, including the index
      // in the array of columns of the oet. Sorted by column index.
      get owningObjectsWithColumnIndex() {
        const oet: IOET = getParentOfType(
          self0,
          types.late((): IAnyModelType => OET)
        );
        return orderBy(
          self.event.owningObjectTypes.map((ot) => ({
            object: ot,
            columnIndex: oet.findColumnIndexByObjectType(ot),
          })),
          ['columnIndex']
        );
      },
      // get the 'first' owning object type, i.e. the one which column is the
      // leftmost in the OET.
      get firstOwningObject(): IObjectType | null {
        if (self.owningObjectsWithColumnIndex.length > 0) {
          return self.owningObjectsWithColumnIndex[0].object;
        } else {
          return null;
        }
      },
      // Get the column index of the first owning object type. Needed for sorting.
      get owningObjectColumnIndex() {
        if (self.owningObjectsWithColumnIndex.length > 0) {
          return self.owningObjectsWithColumnIndex[0].columnIndex;
        } else {
          return Number.MAX_VALUE;
        }
      },
      // Get the 'priority' of the of the row based on the type of the methods for
      // the referenced event type in the first owning object. The order is: CREATE,
      // MODIFY, END. Needed for sorting.
      get owningMethodsPriority() {
        if (self.firstOwningObject !== null) {
          const methodTypes = self.firstOwningObject
            .methodsForEventType(self.event.id)
            .map((m) => m.type);
          if (methodTypes.includes('CREATE')) {
            return 0;
          } else if (methodTypes.includes('MODIFY')) {
            return 1;
          } else {
            return 2;
          }
        } else {
          return Number.MAX_VALUE;
        }
      },
      // Get the index of the row in the array of rows.
      get index() {
        const rows = getParent<IOETRow[]>(self0);
        return rows.indexOf(self);
      },
      // Get a flattened array or the row and its subRows
      get flattenedRows(): IOETRow[] {
        return [self].concat(self.subRows.flatMap((r) => r.flattenedRows.slice()));
      },
      // The group to which to constraint the drag'n'drop of a row
      get dndGroup() {
        if (self.event.specialised) {
          // This is a subrow, constrain drag'n'drop to rows with with same 'ownerEventType'
          return self.event.ownerEventType!.id;
        } else {
          return 'top';
        }
      },
    };
  })
  .actions((self0) => {
    const self = self0 as unknown as IOETRow;
    return {
      addSubRow(event: string) {
        self0.subRows.push({
          event,
        } as unknown as (typeof self0.subRows)[number]);
      },
      removeSubRow(event: string) {
        const row = self.subRows.find((r) => r.event.id === event);
        const cloned = clone(row as unknown as (typeof self0.subRows)[number]);
        self0.subRows.remove(row as unknown as (typeof self0.subRows)[number]);
        return cloned as unknown as IOETRow;
      },
      // move a sub row from index 'from' to index 'to'
      moveSubRow(from: number, to: number) {
        const row = self0.subRows[from];
        const newRow = clone(row);
        self0.subRows.splice(to, 0, newRow);
        self0.subRows.remove(row);
      },
      sortSubRows() {
        if (self.subRows.length > 0) {
          self0.subRows = self0.subRows.slice().sort((first, second) => {
            return compareSiblingRows(
              first as unknown as IOETRow,
              second as unknown as IOETRow
            );
          }) as unknown as typeof self0.subRows;
          self.subRows.forEach((r) => r.sortSubRows());
        }
      },
    };
  });

export default OETRow;
